import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import MainLayout from '../layout/MainLayout';
import ContentArea from '../layout/ContentArea';
import Sidebar from '../layout/Sidebar';
import PillMenu from '../common/menu/PillMenu';
import ServicesList from './ServicesList';
import RightSidebar from '../layout/RightSidebar';
import CreateServiceForm from './service-details/CreateServiceForm';
import PillButton from '../common/button/PillButton';

import styles from './styles/StoreContainer.module.css';

const TABS = [
  { id: 'discover', label: 'Discover' },
  { id: 'added', label: 'Added Services' },
  { id: 'owned', label: 'Owned Services' },
];

const TAB_PATHS = {
  discover: '/store/discover',
  added: '/store/added',
  owned: '/store/owned',
};

const StoreContainer = () => {
  const [activeTab, setActiveTab] = useState(null);
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { setPage: setSidebarPage } = useSidebar();

  const getInitialTab = useCallback(() => {
    const path = location.pathname;
    const tab = Object.entries(TAB_PATHS).find(([_, url]) => url === path);
    const savedTab = localStorage.getItem('storeActiveTab');

    if (tab) {
      return tab[0];
    } else if (savedTab && TAB_PATHS[savedTab]) {
      navigate(TAB_PATHS[savedTab]);
      return savedTab;
    }
    return 'discover';
  }, [location, navigate]);

  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [getInitialTab]);

  useEffect(() => {
    document.title = 'JetChats - Store';
    setSidebarPage('store');
  }, [setSidebarPage]);

  const handleNewChat = useCallback(() => {
    navigate('/chats');
  }, [navigate]);

  const handleCreateService = useCallback(() => {
    setIsCreateServiceOpen(true);
  }, []);

  const handleCloseCreateService = useCallback(() => {
    setIsCreateServiceOpen(false);
  }, []);

  const handleNewServiceCreated = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    // Make sure we're on the owned tab to see the new service
    if (activeTab !== 'owned') {
      setActiveTab('owned');
    }
  }, [activeTab]);

  const handleTabChange = useCallback(tabId => {
    setActiveTab(tabId);
    localStorage.setItem('storeActiveTab', tabId);
    navigate(TAB_PATHS[tabId]);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/store') {
      navigate(TAB_PATHS.discover);
    }
  }, [location.pathname, navigate]);

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'discover':
        return <ServicesList key={`discover-list-${refreshKey}`} type="discover" />;
      case 'added':
        return <ServicesList key={`added-services-list-${refreshKey}`} type="added" />;
      case 'owned':
        return <ServicesList key={`owned-services-list-${refreshKey}`} type="owned" />
      default:
        return null;
    }
  }, [activeTab, refreshKey]);

  return (
    <MainLayout>
      <Sidebar handleNewChat={handleNewChat} />
      <ContentArea>
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.header}>
              <h1>Store</h1>
              <p>Discover and enable services to enhance your chat experience.</p>
            </div>

            <div className={styles.tabRow}>
              {activeTab === 'owned' ? (
                <>
                  <div className={styles.tabsWrapper}>
                    <PillMenu tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
                  </div>
                  <PillButton
                    onClick={handleCreateService}
                    size="medium"
                    title="Create your own service"
                  >
                    Create an Own Service
                  </PillButton>
                </>
              ) : (
                <div className={styles.tabsWrapper}>
                  <PillMenu tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
                </div>
              )}
            </div>

            {renderTabContent()}
          </div>
        </div>
      </ContentArea>

      <RightSidebar
        isOpen={isCreateServiceOpen}
        onClose={handleCloseCreateService}
      >
        <CreateServiceForm
          isOpen={isCreateServiceOpen}
          onClose={handleCloseCreateService}
          onServiceCreated={handleNewServiceCreated}
        />
      </RightSidebar>
    </MainLayout>
  );
};

export default memo(StoreContainer);
